import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'
import type { SavedFilterPermission, SavedFilterScope } from '@/lib/types/savedFilter'

/**
 * Saved filter document interface
 */
export interface SavedFilterDocument extends BaseDocument {
  name: string
  description: string | null
  filterConfig: string
  layoutConfig: string | null
  scope: SavedFilterScope
  scopeEntityId: string | null
  permission: SavedFilterPermission
  ownerId: string
  icon: string | null
  sortOrder: string | null
}

/**
 * Saved filter hooks
 */
const hooks = createEntityHooks<SavedFilterDocument>('savedfilters')

export const useSavedFilters = (filter?: Partial<SavedFilterDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  return { savedFilters: data, isLoading, db }
}

export const useSavedFilter = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { savedFilter: data, isLoading }
}

export const useSavedFilterMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createSavedFilter: create,
    updateSavedFilter: update,
    deleteSavedFilter: deleteEntity,
  }
}

/**
 * Get saved filters for a specific project, visible to the current user.
 */
export const useProjectSavedFilters = (projectId: string | undefined, userId: string | undefined) => {
  const { savedFilters, isLoading, db } = useSavedFilters()
  const filtered = projectId && userId
    ? savedFilters?.filter((f) =>
        f.scope === 'project' &&
        f.scopeEntityId === projectId &&
        (f.permission !== 'private' || f.ownerId === userId),
      ) ?? null
    : null
  return { savedFilters: filtered, isLoading, db }
}

/**
 * Get saved filters for a specific space, visible to the current user.
 */
export const useSpaceSavedFilters = (spaceId: string | undefined, userId: string | undefined) => {
  const { savedFilters, isLoading, db } = useSavedFilters()
  const filtered = spaceId && userId
    ? savedFilters?.filter((f) =>
        f.scope === 'space' &&
        f.scopeEntityId === spaceId &&
        (f.permission !== 'private' || f.ownerId === userId),
      ) ?? null
    : null
  return { savedFilters: filtered, isLoading, db }
}

/**
 * Get global (workspace-wide) saved filters, visible to the current user.
 */
export const useGlobalSavedFilters = (userId: string | undefined) => {
  const { savedFilters, isLoading, db } = useSavedFilters()
  const filtered = userId
    ? savedFilters?.filter((f) =>
        f.scope === 'global' &&
        (f.permission !== 'private' || f.ownerId === userId),
      ) ?? null
    : null
  return { savedFilters: filtered, isLoading, db }
}
